import React, { useState } from 'react';
import { Input } from './ui/input';
import { Upload, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable image field: toggle between local upload and external URL, with preview.
const ImageUploader = ({ value, onChange, uploadEndpoint, adminAuth, label = 'Image' }) => {
  const [mode, setMode] = useState(value && value.startsWith('http') ? 'url' : 'upload');
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}${uploadEndpoint}`, {
        method: 'POST',
        headers: { Authorization: `Basic ${adminAuth}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        onChange(data.url);
        toast.success('Image uploaded');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium">{label}</label>
        <div className="flex gap-1">
          <button
            type="button"
            data-testid="image-mode-upload"
            onClick={() => setMode('upload')}
            className={`px-2 py-1 text-xs rounded ${mode === 'upload' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
          >
            <Upload className="w-3 h-3 inline mr-1" />
            Upload
          </button>
          <button
            type="button"
            data-testid="image-mode-url"
            onClick={() => setMode('url')}
            className={`px-2 py-1 text-xs rounded ${mode === 'url' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
          >
            <LinkIcon className="w-3 h-3 inline mr-1" />
            URL
          </button>
        </div>
      </div>

      {mode === 'upload' ? (
        <div className="space-y-2">
          <input
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            disabled={uploading}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 disabled:opacity-50"
          />
          {uploading && <p className="text-xs text-amber-600">Uploading...</p>}
        </div>
      ) : (
        <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="https://..." />
      )}

      {value && (
        <div className="relative w-full h-28 rounded-lg overflow-hidden bg-gray-100">
          <img
            src={getFullUrl(value)}
            alt="Preview"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
